/**
 * Reconciles the round's headline figures from the SERVER's per-round counter
 * and this client's own rows.
 *
 * WHY. The panel used to count the round purely from the rows it happened to
 * hold. That hid its own failures: whenever bets were lost — a cluster island
 * during a socket-server rollout, the join-time `round:snapshot` race, a
 * terminal `phx_close` — the headline shrank in step with the list, so nothing
 * on screen contradicted it and a broken feed read as a quiet round.
 *
 * The server's figures ride on every `bet` broadcast and are already parsed
 * into the social store as `totalBets` / `totalAmount`; they were simply never
 * read. They also SELF-HEAL: a bet whose broadcast this client missed is still
 * included in the next broadcast's cumulative total, whereas a client-side sum
 * can never recover a drop.
 *
 * DELIBERATE CONSEQUENCE: the meter can read "12 bets" over 9 listed rows.
 * That is the correct trade — it surfaces a shortfall instead of hiding it —
 * and it matches how the classic marble list already behaved (server total
 * above a capped list). `missingCount` exposes the gap so it can be logged
 * rather than silently absorbed.
 */
export interface RoundCounts {
  /** Bets in the round: the server's figure, floored by what we actually hold. */
  totalCount: number
  /** Staked in the round, same flooring. USD wire units. */
  stakedTotal: number
  /** Winners so far — client-derived, clamped to totalCount. */
  winnersCount: number
  /** Winners share of the round, 0..1. */
  progress: number
  /** Rows the server counted that this client never received. */
  missingCount: number
}

export const resolveRoundCounts = (input: {
  serverTotalBets: number
  serverTotalAmount: number
  clientCount: number
  clientStaked: number
  winnersCount: number
}): RoundCounts => {
  // max(), not "trust the server": a mid-round joiner has totalBets = 0 until
  // the next bet lands, because `round:snapshot` carries no totals. Without the
  // floor the panel would show "0 bets" above a full list.
  const totalCount = Math.max(input.serverTotalBets, input.clientCount)
  const stakedTotal = Math.max(input.serverTotalAmount, input.clientStaked)

  // Winners have no server aggregate — bet:won / bet:cashed_out carry no
  // totals — so the numerator can briefly outrun a stale denominator. Left
  // unclamped that pushes the progress fill past 100%.
  const winnersCount = Math.min(input.winnersCount, totalCount)

  return {
    totalCount,
    stakedTotal,
    winnersCount,
    progress: totalCount > 0 ? winnersCount / totalCount : 0,
    missingCount: Math.max(0, input.serverTotalBets - input.clientCount)
  }
}
